import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import ChevronIcon from '~/components/atoms/icon/chevron/ChevronIcon';
import ShowMoreButton from '~/components/molecules/showMoreButton/showMoreButton';
import typography from '~/components/styles/typography.module.scss';
import styles from './accordion.module.scss';

const updateHeight = (
    content: HTMLElement | null,
    isOpen: boolean,
    setHeight: React.Dispatch<React.SetStateAction<string>>
) => {
    if (isOpen && content && content.scrollHeight > 0) {
        setHeight(`${content.scrollHeight}px`);
    }
};

export interface IAccordion extends React.PropsWithChildren {
    label: string;
    showOpen: boolean;
    showMoreCount: number;
    showChevron: boolean;
    showAllValues: boolean;
    showMoreButton: boolean;
    setShowAllValues: React.Dispatch<React.SetStateAction<boolean>>;
    headClassName?: string;
    isCollapsed?: boolean;
    showMoreButtonAttributes?: React.HTMLAttributes<HTMLButtonElement> & { 'data-testid': string };
    labelAttributes?: React.HTMLAttributes<HTMLDivElement> & { 'data-testid': string };
}

const Accordion: React.FC<IAccordion> = ({
    label,
    showOpen,
    showMoreCount,
    showChevron = true,
    showAllValues,
    showMoreButton,
    setShowAllValues,
    children,
    headClassName,
    showMoreButtonAttributes,
    labelAttributes,
}) => {
    const [isOpen, setOpenState] = useState(showOpen);
    const [height, setHeight] = useState(showOpen ? 'unset' : '0px');
    const [chevronRotation, setChevronRotation] = useState<'down' | 'up'>(showOpen ? 'up' : 'down');

    const content = useRef<HTMLDivElement>(null);

    const toggleAccordion = (): void => {
        if (isOpen) {
            setOpenState(false);
            setHeight('0px');
            setChevronRotation('down');
            setShowAllValues(false);
        } else {
            setOpenState(true);
            updateHeight(content.current, isOpen, setHeight);
            setChevronRotation('up');
        }
    };

    const handleShowAllValues = () => {
        setShowAllValues(!showAllValues);

        setTimeout(() => {
            updateHeight(content.current, isOpen, setHeight);
        }, 50);
    };

    useEffect(() => {
        if (!content.current || !isOpen) {
            return;
        }

        updateHeight(content.current, isOpen, setHeight);

        const resizeObserver = new ResizeObserver(() => {
            updateHeight(content.current, isOpen, setHeight);
        });

        resizeObserver.observe(content.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isOpen]);

    return (
        <>
            <div
                className={classNames(styles.accordionSection)}
                data-testid="accordion-button-arrow"
                role="button"
                tabIndex={0}
                onClick={toggleAccordion}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleAccordion();
                    }
                }}
            >
                <div
                    role="heading"
                    aria-level={3}
                    className={classNames(typography.serpUppercaseTitle, headClassName)}
                    {...labelAttributes}
                >
                    {label}
                </div>
                {showChevron ? (
                    <span className={classNames(styles.accordionChevronIcon)}>
                        <ChevronIcon width={8} direction={chevronRotation} />
                    </span>
                ) : null}
            </div>
            <div
                className={classNames({
                    [styles.accordionContent]: true,
                    [styles.accordionContentShowMore]: showAllValues,
                })}
                style={{ maxHeight: `${height}` }}
            >
                {/* we need to wrap children in a div to get the height of the children. Because the parent div's height is resctricted by its max-height, we need to observe the child instead */}
                <div ref={content}>{children}</div>
            </div>
            {0 < showMoreCount && isOpen && showMoreButton ? (
                <ShowMoreButton
                    showMoreCount={showMoreCount}
                    showAllValues={showAllValues}
                    onClickHandler={handleShowAllValues}
                    {...showMoreButtonAttributes}
                />
            ) : null}
        </>
    );
};

export default Accordion;
